import { useEffect } from "react";
import { FormControl, Button, useMediaQuery } from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
//import AddShopingCartIcon from '@mui/icons-material/AddShoppingCart';
import RestaurantIcon from '@mui/icons-material/Restaurant';
//import DownloadPDF from '@mui/icons-material/PictureAsPdf';
import { useShallow } from "zustand/react/shallow";

import ContentLayout from "~/components/ContentLayout";
import LoggedContent from "~/components/LoggedContent";
import Loader from "~/components/Loader";
import Calendar, { ICalendarDayData } from "~/components/Calendar";
import { beDateToLocal, dateToBe, getBeDates, getDateMonthTitle, getDateRangeTitle, getDayTitle, getNextMonth, getNextMonthEnd, getPrevMonth, parseBeDate } from "~/utils/date";
import { SCHEDULE_RANGE } from "~/const";
import { userStore } from "~/stores/user";
import { notificationStore } from "~/stores/notification";
import { listMenu, generateMenu, userGoalEnergy, /*getShopingListPdf, */getShoppingList } from "~/providers/api";
import { /*getRandomValue, */mergeCalendarItems } from "~/utils/utils";
import DateInput from "~/components/DateInput";
import Meals from "~/components/Meals";
import NutritionInfo from "~/components/NutritionInfo";
import MobileWeek from "~/components/MobileWeek";
import { IGetMenuRecipeItem, IShoppingListItem } from "~/providers/interfaces";
import RecipesPreviewModal from "~/modals/RecipesPreviewModal";
import ShoppingListModal from "~/modals/ShoppingListModal";
import { myUseState } from "~/hooks/myUseState";
import Cookies from "js-cookie";
import DonationModal from "~/modals/DonationModal";

import "./style.less";
import { trans } from "~/utils/trans";

type TSelectionType = "buy-date" | "buy-selection" | "selection-error" | "generate-menu" | "pdf-shopping" | "paid-day" | "old-date";

const MAX_DAYS = 7;
// telefony - misto mesicniho kalendare tyden se dny pod sebou
const PHONE_MEDIA_QUERY = "(max-width: 599px)";
const DONATION = {
	cookieName: "difyDonation",
	// 7 days
	/* eslint-disable-next-line no-magic-numbers */
	duration: 7 * 24 * 3600 * 1000,
};

interface IState {
	// kalendar
	curDate: string,
	selected: Array<string>;
	data: Array<ICalendarDayData>;
	calendarInd: number;
	// vybrany datum
	calendarMonth: string;
	// vybrany rozsah z kalendare
	selDate: Array<string>;
	// vybrana data dne
	selData: ICalendarDayData;
	selType: TSelectionType;
	// global
	loading: boolean;
	recipeDetail: IGetMenuRecipeItem;
	shoppingListItems: Array<IShoppingListItem>;
	showDonation: boolean;
	// denni cil kalorii uzivatele
	goalEnergy: number;
}

function getSelectionData(dates: Array<string>, data: Array<ICalendarDayData>): Pick<IState, "selType" | "selData" | "selDate"> {
	const datesLen = dates.length;
	const date = new Date();
	const curDate = dateToBe(date);
	// 1 den

	if (datesLen === 1) {
		const filtered = data.filter(item => item.beDate === dates[0]);
		let selType: TSelectionType = filtered.length === 1
			? filtered[0].hasMeals
				? "paid-day"
				: "generate-menu"
			: "buy-date";

		if (selType === "buy-date" && (dates[0]<curDate)) {
			selType = "old-date";
		}

		return {
			selType,
			selData: filtered.length === 1 ? filtered[0] : null,
			selDate: dates,
		};
	}

	let generatedCount = 0;
	let mealsCount = 0;
	let oldNotPaidDay = false;

	for (let ind = 0, max = datesLen; ind < max; ind++) {
		const filtered = data.filter(item => item.beDate === dates[ind]);

		if (filtered.length === 1) {
			if (filtered[0].isPaid) {
				if (filtered[0].hasMeals) {
					mealsCount++;
				} else {
					generatedCount++;
				}
			}
		} else if (dates[ind] < curDate) {
			oldNotPaidDay = true;
		}
	}

	let selType: TSelectionType = "buy-selection";

	if (datesLen === mealsCount) {
		selType = "pdf-shopping";
	} else if (datesLen === generatedCount) {
		selType = "generate-menu";
	} else if (generatedCount > 0 || mealsCount > 0) {
		selType = "selection-error";
	} else if (oldNotPaidDay) {
		selType = "old-date";
	}

	return {
		selType,
		selData: null,
		selDate: dates,
	};
}

export default function SchedulePage() {
	const { userData } = userStore(useShallow(userState => ({
		userData: userState.user,
	})));
	const setNotification = notificationStore.getState().setNotification;
	const { state, setState, updateState } = myUseState<IState>(() => {
		const date = new Date();
		const curDate = dateToBe(date);

		return {
			...getSelectionData([curDate], []),
			curDate,
			selDate: [curDate],
			calendarMonth: getDateMonthTitle(date),
			calendarInd: SCHEDULE_RANGE.middle,
			selected: [curDate],
			data: [],
			loading: true,
			recipeDetail: null,
			shoppingListItems: [],
			goalEnergy: 0,
		};
	});

	function setPrevMonth() {
		if (state.calendarInd < SCHEDULE_RANGE.middle) {
			return;
		}

		const newDate = getPrevMonth(parseBeDate(state.curDate));

		setState(prev => ({
			...prev,
			curDate: dateToBe(newDate),
			calendarMonth: getDateMonthTitle(newDate),
			calendarInd: prev.calendarInd - 1,
		}));
	}

	function setNextMonth() {
		if (state.calendarInd > SCHEDULE_RANGE.middle) {
			return;
		}

		const newDate = getNextMonth(parseBeDate(state.curDate));

		setState(prev => ({
			...prev,
			curDate: dateToBe(newDate),
			calendarMonth: getDateMonthTitle(newDate),
			calendarInd: prev.calendarInd + 1,
		}));
	}

	function onSelection(dates: Array<string>) {
		setState(prev => ({
			...prev,
			...getSelectionData(dates, prev.data),
		}));
	}

	function getMetaTitle() {
		const first = parseBeDate(state.selDate[0]);
		const last = state.selDate.length > 1 ? parseBeDate(state.selDate[state.selDate.length - 1]) : null;

		return last ? getDateRangeTitle(first, last, undefined, true) : getDayTitle(first);
	}

	function updateRange(position: "from" | "to", beDate: string) {
		const newRange = position === "from"
			? getBeDates(beDate, state.selDate[state.selDate.length - 1])
			: getBeDates(state.selDate[0], beDate);

		setState(prev => ({
			...prev,
			selected: newRange,
			selDate: newRange,
			...getSelectionData(newRange, prev.data),
		}));
	}

	async function loadData(updateSelectionData?: boolean) {
		const [listMenuData, goalEnergy] = await Promise.all([
			listMenu(userData.jwt, userData.id),
			userGoalEnergy(userData.jwt, userData.id),
		]);
		const errors = [
			...listMenuData.errors,
		];
		const mergedData = mergeCalendarItems(listMenuData.data);
		const donationCookie = Cookies.get(DONATION.cookieName);
		const donationValue = donationCookie
			? parseInt(donationCookie)
			: 0;
		let showDonation = false;

		if (!donationValue || donationValue < Date.now()) {
			const expires = Date.now() + DONATION.duration;

			Cookies.set(DONATION.cookieName, expires.toString(), {
				expires: new Date(expires),
				sameSite: "Strict",
				path: "/",
				domain: `.${location.hostname}`,
			});
			showDonation = true;
		}

		setState(prev => ({
			...prev,
			data: mergedData,
			loading: false,
			showDonation,
			goalEnergy,
			...updateSelectionData
				? {
					...getSelectionData(prev.selDate, mergedData),
				}
				: {},
		}));

		if (errors.length) {
			setNotification(errors.join(", "), "error");
		}
	}

	async function clickGenerateMenu() {
		const fromDate = state.selDate[0];

		let toDate = "";

		if (state.selDate.length > MAX_DAYS) {
			toDate = state.selDate[MAX_DAYS - 1];
			setNotification(trans("notifications.schedule.maxDays", { max: MAX_DAYS }));
		} else if (state.selDate.length >= 1 && state.selDate.length <= MAX_DAYS) {
			toDate = state.selDate[state.selDate.length - 1];
			//setNotification(state.selDate.length.toString());
		}
		if (userData.errors.length > 0) {
			setNotification(trans("notifications.profile.fillRequired"));

			return;
		}
		//toDate = state.selDate.length > 1 ? state.selDate[state.selDate.length - 1] : "";
		const generateData = await generateMenu(userData.jwt, userData.id, fromDate, toDate);

		if (generateData.noErrors) {
			setNotification(trans("notifications.schedule.generated"));
			loadData(true);
		} else {
			setNotification(generateData.errors.join(", "), "error");
		}
	}

	/*async function generatePdf() {
		const fromDate = state.selDate[0];
		const toDate = state.selDate[state.selDate.length - 1];
		const listError = await getShopingListPdf(userData.jwt, userData.id, fromDate, toDate, trans("schedule.shoppingPdf"));

		// chyba
		if (listError) {
			console.log(listError);
			setNotification(listError, "error");
		}
	}*/

	async function clickShoppingList() {
		const fromDate = state.selDate[0];
		const toDate = state.selDate[state.selDate.length - 1];
		const shoppingListData = await getShoppingList(userData.jwt, userData.id, fromDate, toDate);

		if (shoppingListData.noErrors) {
			updateState({
				shoppingListItems: shoppingListData.data,
			});
		} else {
			setNotification(shoppingListData.errors.join(", "), "error");
		}
	}

	async function onBuy() {
		const fromDate = state.selDate[0];

		if (userData.errors.length > 0) {
			setNotification(trans("notifications.profile.fillRequired"));

			return;
		}

		let toDate = "";

		if (state.selDate.length > MAX_DAYS) {
			toDate = state.selDate[MAX_DAYS - 1];
			setNotification(trans("notifications.schedule.maxDays", { max: MAX_DAYS }));
		} else if (state.selDate.length >= 1 && state.selDate.length <= MAX_DAYS) {
			toDate = state.selDate[state.selDate.length - 1];
		}

		const menuData = await generateMenu(userData.jwt, userData.id, fromDate, toDate);
		const errors = [
			...menuData.errors,
		];

		if (errors.length === 0) {
			loadData(true);
		} else {
			setNotification(errors.join(", "), "error");
		}
	}

	// telefon: sestaveni jidelnicku pro rozsah z MobileWeek (ten hlida nejvys 7 dni), true = sestaveno
	async function generateRange(fromDate: string, toDate: string) {
		if (userData.errors.length > 0) {
			setNotification(trans("notifications.profile.fillRequired"));

			return false;
		}

		const menuData = await generateMenu(userData.jwt, userData.id, fromDate, toDate);

		if (!menuData.noErrors) {
			setNotification(menuData.errors.join(", "), "error");

			return false;
		}

		setNotification(trans("notifications.schedule.generated"));
		loadData(true);

		return true;
	}

	// telefon: nakupni seznam pro rozsah z MobileWeek
	async function showShoppingList(fromDate: string, toDate: string) {
		const shoppingListData = await getShoppingList(userData.jwt, userData.id, fromDate, toDate);

		if (!shoppingListData.noErrors) {
			setNotification(shoppingListData.errors.join(", "), "error");
		} else if (shoppingListData.data.length === 0) {
			setNotification(trans("notifications.schedule.shoppingEmpty"));
		} else {
			updateState({
				shoppingListItems: shoppingListData.data,
			});
		}
	}

	useEffect(() => {
		if (userData.jwt) {
			loadData(true);
		}
	}, [userData.jwt]);

	const isPhone = useMediaQuery(PHONE_MEDIA_QUERY);


	const mainContent = <div className="schedulePage" slot="mainContent">
		<LoggedContent unauthorized={true}>
			<div className="scheduleHeader">
				<h1 style={{ marginBottom: "10px" }}>
					{state.calendarMonth}
				</h1>
				<div className="control">
					<Button onClick={setPrevMonth} className="schedulePage__montChangeBtn" disabled={state.calendarInd === SCHEDULE_RANGE.left}>
						<ArrowBackIosNewIcon className="MyArrow"/>
					</Button>
					<Button onClick={setNextMonth} className="schedulePage__montChangeBtn" disabled={state.calendarInd === SCHEDULE_RANGE.right}>
						<ArrowForwardIosIcon className="MyArrow"/>
					</Button>
				</div>
			</div>
			{ !state.loading && <Calendar curDate={state.curDate} data={state.data} selected={state.selected} onSelection={onSelection} /> }
			{ state.loading && <Loader /> }
			{ state.recipeDetail && <RecipesPreviewModal recipe={state.recipeDetail} onClose={() => updateState({ recipeDetail: null })} /> }
			{ state.shoppingListItems.length > 0 && <ShoppingListModal items={state.shoppingListItems} onClose={() => updateState({ shoppingListItems: [] })} /> }
		</LoggedContent>
	</div>;
	const rightContent = <div className="scheduleMeta">
		<LoggedContent unauthorized={false}>
			{ (state.selType === "buy-selection" || state.selType === "buy-date") && <>
				<h2>{trans("schedule.menuCreation")}</h2>
				<div className="scheduleMeta__separator" />
				<p className="scheduleMeta__desc">{ getMetaTitle() }</p>
				<div>
					<FormControl sx={{ m: 1, width: "96%" }}>
						<DateInput
							label={trans("common.from")}
							onChange={(date, data) => updateRange("from", data.be)}
							value={beDateToLocal(state.selDate[0])}
							minDate={getPrevMonth(parseBeDate(state.curDate))}
							maxDate={getNextMonthEnd(parseBeDate(state.curDate))}
						/>
					</FormControl>
				</div>
				<div>
					<FormControl sx={{ m: 1, width: "96%" }}>
						<DateInput
							label={trans("common.to")}
							onChange={(date, data) => updateRange("to", data.be)}
							value={beDateToLocal(state.selDate[state.selDate.length - 1])}
							minDate={getPrevMonth(parseBeDate(state.curDate))}
							maxDate={getNextMonthEnd(parseBeDate(state.curDate))}
						/>
					</FormControl>
				</div>
				<div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
					<span>{trans("schedule.totalDays")} <strong>{state.selDate.length}</strong></span>
				</div>
				<div className="scheduleMeta__buttonsArea">
					<Button className="MyButton" onClick={onBuy} variant="contained" startIcon={<RestaurantIcon />}>{trans("schedule.generateMenu")}</Button>
				</div>
			</> }
			{ state.selType === "paid-day" && <>
				<h2>{trans("schedule.yourDay", { date: getMetaTitle() })}</h2>
				<div className="scheduleMeta__separator" />
				<Meals
					list={state.selData.menuData.menu}
					date={state.selDate[0]}
					allMenus={state.data.filter(day => day.menuData).map(day => day.menuData)}
					openRecipe={recipeDetail => updateState({ recipeDetail })}
					onRegenerated={() => loadData(true)}
					variant="list"
				/>
				<NutritionInfo
					variant="day"
					goalEnergy={state.goalEnergy}
					energy={state.selData.menuData.energy}
					proteins={state.selData.menuData.proteins}
					carbohydrates={state.selData.menuData.carbohydrates}
					fats={state.selData.menuData.fats}
				/>
				<div className="scheduleMeta__separator" />
				<div className="scheduleMeta__buttonsArea">
					<Button className="MyButton" onClick={clickShoppingList} variant="contained" startIcon={<ShoppingBasketIcon />}>{trans("schedule.showShoppingList")}</Button>
					{/*<Button className="MyButton" onClick={generatePdf} variant="contained" startIcon={<DownloadPDF />}>Stáhnout nákupní seznam</Button>*/}
				</div>
			</> }
			{ state.selType === "selection-error" && <>
				<h2>{trans("schedule.days", { date: getMetaTitle() })}</h2>
				<div className="scheduleMeta__buttonsArea">
					<div className="scheduleMeta__separator" />
					<p className="scheduleMeta__desc">{trans("schedule.selectionError")}</p>
					<div className="scheduleMeta__separator" />
				</div>
			</> }
			{ state.selType === "old-date" && <>
				<h2>{ getMetaTitle() }</h2>
				<div className="scheduleMeta__buttonsArea">
					<div className="scheduleMeta__separator" />
					<p className="scheduleMeta__desc">{trans("schedule.oldDate")}</p>
					<div className="scheduleMeta__separator" />
				</div>
			</> }
			{ state.selType === "generate-menu" && <>
				<p className="scheduleMeta__desc">{trans("schedule.menuCreation")}</p>
				<div className="scheduleMeta__buttonsArea">
					<Button className="MyButton" onClick={clickGenerateMenu} variant="contained">{trans("schedule.generateMenu")}</Button>
				</div>
			</> }
			{ (state.selType === "pdf-shopping") && <>
				<h2>{trans("schedule.days", { date: getMetaTitle() })}</h2>
				<div className="scheduleMeta__buttonsArea">
					<div className="scheduleMeta__separator" />
					<p className="scheduleMeta__desc">{trans("schedule.shoppingDesc")}</p>
					<div className="scheduleMeta__separator" />
					<Button className="MyButton" onClick={clickShoppingList} variant="contained" startIcon={<ShoppingBasketIcon />}>{trans("schedule.shoppingList")}</Button>
					{/*<Button className="MyButton" onClick={generatePdf} variant="contained" startIcon={<DownloadPDF />}>Stáhnout pdf</Button>*/}

				</div>
			</>}
			{ state.showDonation && <DonationModal onClose={() => updateState({ showDonation: false })} /> }
		</LoggedContent>
	</div>;

	if (isPhone) {
		const phoneContent = <div className="schedulePage schedulePage--phone" slot="mainContent">
			<LoggedContent unauthorized={true}>
				{ state.loading
					? <Loader />
					: <MobileWeek
						data={state.data}
						goalEnergy={state.goalEnergy}
						onGenerate={generateRange}
						onShoppingList={showShoppingList}
						openRecipe={recipeDetail => updateState({ recipeDetail })}
						onRegenerated={() => loadData(true)}
					/> }
				{ state.recipeDetail && <RecipesPreviewModal recipe={state.recipeDetail} onClose={() => updateState({ recipeDetail: null })} /> }
				{ state.shoppingListItems.length > 0 && <ShoppingListModal items={state.shoppingListItems} onClose={() => updateState({ shoppingListItems: [] })} /> }
				{ state.showDonation && <DonationModal onClose={() => updateState({ showDonation: false })} /> }
			</LoggedContent>
		</div>;

		return <ContentLayout className="layout__schedule" mainContent={phoneContent} />;
	}

	return <ContentLayout className="layout__schedule" mainContent={mainContent} rightContent={rightContent} />;
}
